import * as React from "react"
import { useEffect, useState, useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
  LayoutDashboard,
  Rocket,
  Users,
  MessageCircle,
  Calendar,
  Wrench,
  Sparkles,
  GraduationCap,
  UserCircle,
  FileText,
  Bot,
  Target,
  Send,
  Briefcase,
  BookOpen,
  Smartphone,
  UserPlus,
  MessageSquareText,
  Megaphone,
  Lightbulb,
  Instagram as InstagramIcon
} from "lucide-react"

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar"
import { NavMain } from "@/components/nav-main"
import { NavUser } from "@/components/nav-user"
import { useBranding } from "@/hooks/useTenant"

interface AppSidebarProps extends React.ComponentProps<typeof Sidebar> {
  hoverMode?: boolean
}

export function AppSidebar({ hoverMode = false, ...props }: AppSidebarProps) {
  const { t } = useTranslation()
  const brand = useBranding()
  const { setOpen } = useSidebar()

  const isTest56 = brand.orgName === 'Test 56'

  const sponsorName = useMemo(() => {
    if (brand.orgName === 'Aurum') return 'AURUM IA Sponsor'
    if (brand.orgName === 'GLN | IA') return 'GLN IA Sponsor'
    return `${brand.orgName} IA Sponsor`
  }, [brand.orgName])

  // Nueva estructura orientada a objetivos del usuario - con traducciones
  const navSections = useMemo(() => [
    {
      label: t('nav.main.principal'),
      items: [
        {
          title: t('nav.main.dashboard'),
          url: "/dashboard",
          icon: LayoutDashboard,
        },
      ],
    },
    {
      label: t('nav.main.startHere'),
      items: [
        {
          title: t('nav.main.launchPlan'),
          url: "/onboarding",
          icon: Rocket,
        },
      ],
    },
    {
      label: t('nav.main.dailyTools'),
      items: [
        {
          title: t('nav.main.whatsapp'),
          icon: MessageCircle,
          items: [
            {
              title: t('nav.main.whatsappConnection'),
              url: "/setter/whatsapp",
            },
            {
              title: t('nav.main.messageTemplates'),
              url: "/setter/whatsapp-templates",
            },
            {
              title: t('nav.main.agentesIA'),
              url: "/setter/agentes-ia",
            },
            {
              title: t('nav.main.blacklist'),
              url: "/setter/blacklist",
            },
          ],
        },
        {
          title: t('nav.main.instagram'),
          icon: InstagramIcon,
          items: [
            {
              title: t('nav.main.templates'),
              url: "/connector/templates",
            },
            {
              title: t('nav.main.campaigns'),
              url: "/connector/campaigns",
            },
          ],
        },
        {
          title: t('nav.main.myContacts'),
          icon: Users,
          items: [
            {
              title: t('nav.main.relationalCapital'),
              url: "/crm/capital-relacional",
            },
            {
              title: t('nav.main.contacts'),
              url: "/crm/dashboard",
            },
          ],
        },
        {
          title: t('nav.main.calendar'),
          url: "/calendar",
          icon: Calendar,
        },
        {
          title: t('nav.main.tutorials'),
          url: "/library/tutorials",
          icon: GraduationCap,
        },
      ],
    },
    {
      label: t('nav.main.contentAndSocialMedia'),
      items: [
        {
          title: t('nav.main.professionalizeProfile'),
          icon: UserCircle,
          items: [
            {
              title: t('nav.main.optimizeProfile'),
              url: "/path2/profile-optimizer",
            },
            {
              title: t('nav.main.photoGenerator'),
              url: "/path2/photo-generator",
            },
          ],
        },
        {
          title: t('nav.main.createPosts'),
          url: "/path2/reels",
          icon: Lightbulb,
        },
      ],
    },
    {
      label: t('nav.main.resourcesAndSupport'),
      items: [
        {
          title: t('nav.main.materialCorporativo'),
          url: "/library/materials",
          icon: Briefcase,
        },
        {
          title: t('nav.main.educativeSystem'),
          url: "/library/tutorials",
          icon: GraduationCap,
        },
        {
          title: t('nav.main.iaPrompts'),
          url: "/admin/prompts",
          icon: Bot,
        },
        {
          title: t('nav.main.knowledgeSources'),
          url: "/admin/knowledge",
          icon: BookOpen,
        },
      ],
    },
  ], [t, sponsorName])

  const isAurum = brand.orgName === 'Aurum'
  const hiddenLabels = new Set([t('nav.main.resourcesAndSupport'), ...(isAurum ? [t('nav.main.contentAndSocialMedia')] : [])])
  const hiddenTitles = useMemo(() => new Set([t('nav.main.materialCorporativo'), t('nav.main.educativeSystem')]), [t])
  const sections = useMemo(() => {
    const toHideForTest56 = new Set([
      t('nav.main.instagram'),
      t('nav.main.professionalizeProfile'),
      t('nav.main.photoGenerator'),
      t('nav.main.optimizeProfile'),
      t('nav.main.createPosts'),
    ])

    const toHideForAurum = new Set([
      t('nav.main.instagram'),
      t('nav.main.calendar'),
    ])

    return navSections
      .map((section) => {
        const baseItems = section.items.filter((it: any) => !hiddenTitles.has(it.title))
        let filteredItems = baseItems
        if (isTest56) {
          filteredItems = filteredItems.filter((it: any) => !toHideForTest56.has(it.title))
        }
        if (isAurum) {
          filteredItems = filteredItems.filter((it: any) => !toHideForAurum.has(it.title))
        }

        return {
          ...section,
          items: filteredItems
        }
      })
      .filter((section) => !hiddenLabels.has(section.label) && section.items.length > 0)
  }, [navSections, hiddenTitles, isTest56, t])

  const hexToHsl = (hex: string) => {
    const cleaned = hex.replace('#', '').trim()
    const full = cleaned.length === 3
      ? cleaned.split('').map((c) => c + c).join('')
      : cleaned

    const r = parseInt(full.slice(0, 2), 16) / 255
    const g = parseInt(full.slice(2, 4), 16) / 255
    const b = parseInt(full.slice(4, 6), 16) / 255

    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    const delta = max - min

    let h = 0
    const l = (max + min) / 2
    const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1))

    if (delta !== 0) {
      if (max === r) h = ((g - b) / delta) % 6
      else if (max === g) h = (b - r) / delta + 2
      else h = (r - g) / delta + 4
      h *= 60
      if (h < 0) h += 360
    }

    return {
      h: Math.round(h),
      s: Math.round(s * 100),
      l: Math.round(l * 100),
    }
  }

  const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))
  const toHslVar = ({ h, s, l }: { h: number; s: number; l: number }) => `${h} ${s}% ${l}%`

  const sidebarStyle = brand.sidebarColor ? (() => {
    const bg = brand.sidebarColor.startsWith('#') ? hexToHsl(brand.sidebarColor) : null
    const fg = brand.sidebarForeground?.startsWith('#')
      ? hexToHsl(brand.sidebarForeground)
      : null

    const background = bg ? toHslVar(bg) : brand.sidebarColor
    const foreground = fg ? toHslVar(fg) : '0 0% 100%'
    const accent = bg ? toHslVar({ ...bg, l: clamp(bg.l - 6, 0, 100) }) : undefined
    const border = bg ? toHslVar({ ...bg, l: clamp(bg.l - 12, 0, 100) }) : undefined

    return {
      '--sidebar-background': background,
      '--sidebar-foreground': foreground,
      '--sidebar-accent': accent || background,
      '--sidebar-accent-foreground': foreground,
      '--sidebar-border': border || background,
    } as React.CSSProperties
  })() : undefined

  return (
    <Sidebar
      collapsible="icon"
      onMouseEnter={hoverMode ? () => setOpen(true) : undefined}
      onMouseLeave={hoverMode ? () => setOpen(false) : undefined}
      style={sidebarStyle}
      {...props}
    >
      <SidebarHeader>
        <div className="flex items-center justify-center px-3 py-4">
          {isTest56 ? (
            <div className="text-sm font-semibold tracking-[0.35em] text-foreground">
              TEST 56
            </div>
          ) : brand.orgName === 'Lumina' ? (
            <img
              src={brand.logoSidebar}
              alt="Logo"
              className="h-32 w-auto object-contain"
            />
          ) : (
            <img
              src={brand.logoSidebar}
              alt="Logo"
              className={
                brand.orgName === 'All Stars Connect'
                  ? 'w-4/5 h-auto object-contain'
                  : brand.orgName === 'EFEX'
                    ? 'h-24 w-auto object-contain'  // 96px - logo grande para EFEX
                    : 'h-8 sm:h-10 w-auto object-contain'
              }
            />
          )}
        </div>
      </SidebarHeader>
      <SidebarContent className="gap-2 py-2 sidebar-scroll">
        {sections.map((section, index) => (
          <NavMain key={index} items={section.items} label={section.label} />
        ))}
      </SidebarContent>
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
